import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { IDriver } from '../driver-side-list/driver-side-list.interface';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DataService, HelperService, HTTPService, ValidationService } from 'app/services';

@Component({
  selector: 'app-driver-side-item-list',
  templateUrl: './driver-side-item-list.component.html',
  styleUrls: ['./driver-side-item-list.component.scss'],
  providers: [DialogService],
  animations: [
    trigger('accordion', [
      state('collapsed', style({ height: '0px', overflow: 'hidden' })),
      state('expanded', style({ height: '*', overflow: 'hidden' })),
      transition('collapsed <=> expanded', [
        animate('50ms ease-in-out')
      ])
    ]),
    trigger('toggleBox', [
      state('open', style({ height: '100px' })),
      state('closed', style({ height: '0px' })),
      transition('open <=> closed', animate('300ms ease-in-out')),
    ]),
  ],
})
export class DriverSideItemListComponent {
  @Input() showForm: boolean = false;
  @Input() customerId: string = '';
  @Input() driver: IDriver;
  @Output() createNewDriver = new EventEmitter<void>();
  @Output() toggleForm = new EventEmitter<void>();

  isLoading = false;
  isSubmitChecked = false;
  isZipCode1Valid: boolean = true;
  error: any = {};
  addressCityResults: any;
  addressCities = [];
  allCities = [];
  originalDriverData = {};
  isOpen = false;
  constructor(private httpService: HTTPService,
    private validationService: ValidationService,
    private dataService: DataService,
    private helperService: HelperService) { }

  validateDriver(driver: IDriver): boolean {
    this.error = {}; // Reset error
    let isValid = true;
    if (!driver.fullName) {
      this.error.fullName = 'Name is required';
      isValid = false;
    }
    const phone = this.helperService.removePhoneFormatting(this.driver.phone);
    const email = this.driver.email;
    if (phone) {
      if ((phone || '').length < 10) {
        this.error.phone = `Phone is not valid`;
        isValid = false;
      }
    }
    if (email) {
      if (email && !this.validationService.validateEmail(email)) {
        this.error.email = 'Email is not valid';
        isValid = false;
      }
    }
    if (this.driver.address.zip && !this.isZipCode1Valid) {
      this.error.zip = 'Zip / Postal Code invalid';
      isValid = false;
    } else {
      this.isZipCode1Valid = true;
    }
    const isFormValid = isValid && this.isZipCode1Valid;
    return isFormValid;
  }

  handleChange(name: string, value: any): void {
    // Support nested properties
    if (name.includes('.')) {
      const [key, subKey] = name.split('.');
      this.driver[key] = {
        ...this.driver[key],
        [subKey]: value,
      }
      // this.driver[key][subKey] = value;
    } else {
      this.driver[name] = value;
    }

    // Validate if submit check is true
    if (this.isSubmitChecked) {
      this.validateDriver(this.driver);
    }

    // Update 'isActive' status via API if changed
    if (name === 'isActive' && this.driver._id) {
      this.httpService.changeIsActive(this.driver._id, this.customerId)
        .then((res) => {
          this.driver.isActive = res.isActive;
          this.dataService.sendData(this.dataService.typePassData('sidebar-driver-update',
            this.driver));
          this.helperService.showToaster('success', 'Driver status updated')
        })
        .catch(err => this.helperService.showToaster('error', err || 'Error updating driver status'));
    }
  }

  toggleFormEmit(): void {
    this.toggleForm.emit();
  }

  onSubmit(): void {
    this.isSubmitChecked = true;

    if (!this.validateDriver(this.driver)) {
      return;
    }

    this.isLoading = true;
    delete this.driver._id;
    this.driver.customerId = this.customerId;
    this.httpService.createDriver(this.driver)
      .then(res => {
        this.isLoading = false;
        this.driver = res; // Update driver with the response data
        this.dataService.sendData(this.dataService.typePassData('sidebar-driver-update',
          this.driver));
        this.helperService.showToaster('success', 'Driver created successfully');
        this.createNewDriver.emit();
      })
      .catch(err => {
        this.helperService.showToaster('error', err || 'Error creating driver');
        this.isLoading = false;
      });
  }

  onUpdate(): void {
    this.isSubmitChecked = true;

    if (!this.validateDriver(this.driver)) {
      return;
    }

    this.isLoading = true;
    this.driver.customerId = this.customerId;
    this.httpService.updateDriver(this.driver)
      .then(() => {
        this.isLoading = false;
        this.dataService.sendData(this.dataService.typePassData('sidebar-driver-update',
          this.driver));
        this.helperService.showToaster('success', 'Driver updated successfully');
      })
      .catch(err => {
        this.helperService.showToaster('error', err || 'Error updating driver');
        this.isLoading = false;
      });
  }

  handleFocus(name: string) {
    this.error[name] = '';
    // this.focus[name] = true;
  }

  handleBlur(name: string) {
    if (name === 'address') {
      this.populateCityByZipCode(name);
    }
    if (name === 'phone') {
      if (!this.driver.phone) {
        this.error.phone = '';
      }
    }
  }

  populateCityByZipCode(type: string) {
    this.addressCities = this.allCities;
    let zip = '';

    if (type === 'address') {
      zip = this.driver.address.zip;
      this.driver.address.zip = this.validationService.formatZip(zip);
    }
    const formattedZip = this.helperService.removeSpaceFromString(zip).toUpperCase();

    this.httpService.getZip(formattedZip).then(
      (res) => {
        if (res) {
          this.isZipCode1Valid = true;
          this.driver.address.city = res.city;
          this.driver.address.state = res.state;
          this.driver.address.stateCode = res.stateCode;
          this.driver.address.country = res.country;
        } else {
          this.driver.address.city = null;
          this.driver.address.state = null;
          if (type === 'address') {
            this.error.zip = 'Zip / Postal Code invalid';
            this.isZipCode1Valid = false;
          }
        }
      },
      (error) => {
        if (type === 'address') {
          this.isZipCode1Valid = false;
          this.error.zip = 'Zip / Postal Code invalid';
        }
      }
    );
  }

  onAddressCitySearch(query: string = '', type: string) {
    if (query) {
      this.httpService.citySearch({ city: this.helperService.trimString(query) }).then(
        (cities) => {
          if (type === 'primary') {
            this.addressCityResults = cities;
          }
        },
        (error) => {
          this.helperService.showToaster('error', this.helperService.getErrorMsg(error));
        }
      );
    }
  }

  onAddressCitySelect(event: any, type: string) {
    this.driver.address.city = event.city;
    this.driver.address.state = event.state;
    this.driver.address.stateCode = event.stateCode;
    this.driver.address.country = event.country;
  }


  renderCityName(city: string, stateCode: string) {
    if (city && stateCode) {
      return `${city}, ${stateCode}`;
    }

    return `${city || ''}`;
  }

  onAddressClear(name: string) {
    if (name === 'primary') {
      this.driver.address.city = '';
    } else {
      this.addressCityResults = [];
    }
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

}
